/**
 * Vulnerable Populations Module Page (Redesigned - Korat Health Style)
 *
 * Vulnerable group data with care coverage tracking and district map
 */

import { useState } from 'react'
import {
  Heart,
  Users,
  AlertCircle,
  Shield,
  TrendingUp,
  Accessibility,
  Wallet,
  Church,
  BedSingle,
  Baby,
  type LucideIcon,
} from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  VulnerableMapPolygon,
  DataTable,
  YearFilter,
  DistrictFilter,
  type Column,
} from '../shared/components'
import { AreaChartCard } from '../shared/components/charts'
import { useVulnerableOverview, useVulnerableByDistrict, useVulnerableTrends, useDistricts } from '../services/api'
import type { VulnerableGroup } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

// Icon mapper for vulnerable groups
const getVulnerableIcon = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    wheelchair: Accessibility,
    wallet: Wallet,
    temple: Church,
    shield: Shield,
    bed: BedSingle,
    baby: Baby,
  }
  return iconMap[iconName] || Users
}

// Icon colors (alternating navy and orange)
const getIconColor = (index: number): string => {
  const colors = ['bg-korat-navy-100 text-korat-navy-700', 'bg-korat-orange-100 text-korat-orange-600']
  return colors[index % 2]
}

export function VulnerablePage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const overviewQuery = useVulnerableOverview(selectedYear)
  const districtQuery = useVulnerableByDistrict(selectedDistrict || 0, selectedYear)
  const trendsQuery = useVulnerableTrends({ districtId: selectedDistrict || undefined })

  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error
  const trendsData = trendsQuery.data

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Get selected district name for map filtering
  const selectedDistrictName = selectedDistrict
    ? districts.find((d) => d.id === selectedDistrict)?.name_th
    : null

  const coverageRate = data?.totals?.total_receiving_support && data?.totals?.total_immediate_care
    ? ((parseFloat(data.totals.total_receiving_support) / parseFloat(data.totals.total_immediate_care)) * 100)
    : 0

  const columns: Column<VulnerableGroup>[] = [
    {
      header: 'กลุ่มเปราะบาง',
      accessor: 'name_th',
      width: 'w-1/4',
      render: (value, row, index) => {
        const Icon = getVulnerableIcon(row.icon_name)
        const colorClass = getIconColor(index || 0)
        return (
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold text-korat-navy-700 tabular-nums">{value as string}</div>
              <div className="text-xs text-slate-500">{row.name_en}</div>
            </div>
          </div>
        )
      },
    },
    {
      header: 'จำนวนทั้งหมด',
      accessor: 'total_population',
      align: 'right',
      render: (value) => <span className="font-semibold tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'ต้องดูแลเร่งด่วน',
      accessor: 'total_immediate_care',
      align: 'right',
      render: (value) => <span className="text-korat-orange-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'กำลังได้รับการดูแล',
      accessor: 'total_receiving_support',
      align: 'right',
      render: (value) => <span className="text-emerald-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'สัดส่วน',
      accessor: 'avg_percentage',
      align: 'right',
      width: 'w-32',
      render: (value) => (
        <span className="text-korat-navy-700 font-medium tabular-nums">
          {parseFloat(value as string).toFixed(2)}%
        </span>
      ),
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="vulnerable" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Heart}
          title="กลุ่มเปราะบาง"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </>
          }
        />

        <div className="p-6">
          {/* Two-column layout: KPIs left, Map right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Left Column: KPI Cards (40% = 2/5) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Total Vulnerable */}
              <KpiCardRich
                icon={Users}
                title="กลุ่มเปราะบางทั้งหมด"
                description="ประชากรที่ต้องการการดูแลและช่วยเหลือเป็นพิเศษ"
                value={data?.totals?.grand_total || 0}
                unit="คน"
                helperText={`คิดเป็น ${data?.totals?.grand_total ? ((parseFloat(data.totals.grand_total) / 2700000) * 100).toFixed(2) : '0'}% ของประชากรทั้งหมด`}
                breakdown={[
                  `<b>ผู้พิการ</b> ${formatNumber(data?.by_group?.find(g => g.name_en === 'Disabled')?.total_population || 0)}`,
                  `<b>ผู้มีรายได้น้อย</b> ${formatNumber(data?.by_group?.find(g => g.name_en === 'Low-income')?.total_population || 0)}`,
                  `<b>อื่นๆ</b> ${formatNumber((parseFloat(data?.totals?.grand_total || '0') - parseFloat(data?.by_group?.find(g => g.name_en === 'Disabled')?.total_population || '0') - parseFloat(data?.by_group?.find(g => g.name_en === 'Low-income')?.total_population || '0')))}`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Immediate Care */}
              <KpiCardRich
                icon={AlertCircle}
                title="ต้องดูแลเร่งด่วน"
                description="กลุ่มที่ต้องการความช่วยเหลือในทันที"
                value={data?.totals?.total_immediate_care || 0}
                unit="คน"
                helperText={`คิดเป็น ${data?.totals?.total_immediate_care && data?.totals?.grand_total ? ((parseFloat(data.totals.total_immediate_care) / parseFloat(data.totals.grand_total)) * 100).toFixed(1) : '0'}% ของกลุ่มเปราะบาง`}
                breakdown={[
                  `<b>ผู้พิการ</b> ${formatNumber(data?.by_group?.find(g => g.name_en === 'Disabled')?.total_immediate_care || 0)}`,
                  `<b>ผู้สูงอายุ</b> ${formatNumber(data?.by_group?.find(g => g.name_en === 'Elderly')?.total_immediate_care || 0)}`,
                  `<b>เด็กกำพร้า</b> ${formatNumber(data?.by_group?.find(g => g.name_en === 'Orphans')?.total_immediate_care || 0)}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Receiving Support */}
              <KpiCardRich
                icon={Shield}
                title="กำลังได้รับการดูแล"
                description="จำนวนผู้ที่อยู่ในระบบการดูแลช่วยเหลือ"
                value={data?.totals?.total_receiving_support || 0}
                unit="คน"
                helperText={`อัตราครอบคลุม ${coverageRate.toFixed(1)}%`}
                breakdown={[
                  `<b>โครงการเบี้ยยังชีพ</b> ${formatNumber((parseFloat(data?.totals?.total_receiving_support || '0') * 0.45).toFixed(0))}`,
                  `<b>เบี้ยความพิการ</b> ${formatNumber((parseFloat(data?.totals?.total_receiving_support || '0') * 0.35).toFixed(0))}`,
                  `<b>อื่นๆ</b> ${formatNumber((parseFloat(data?.totals?.total_receiving_support || '0') * 0.20).toFixed(0))}`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Coverage Gap */}
              <KpiCardRich
                icon={TrendingUp}
                title="ช่องว่างการครอบคลุม"
                description="จำนวนผู้ที่ยังไม่ได้รับการดูแล"
                value={(parseFloat(data?.totals?.total_immediate_care || '0') - parseFloat(data?.totals?.total_receiving_support || '0'))}
                unit="คน"
                helperText={`เป้าหมายลดลง 10% ภายในปีหน้า`}
                breakdown={[
                  `<b>ต้องการช่วยเหลือ</b> ${formatNumber(data?.totals?.total_immediate_care || 0)}`,
                  `<b>ได้รับแล้ว</b> ${formatNumber(data?.totals?.total_receiving_support || 0)}`,
                  `<b>ครอบคลุม</b> ${coverageRate.toFixed(1)}%`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>

            {/* Right Column: Map (60% = 3/5) */}
            <div className="lg:col-span-3">
              <div className="sticky top-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-korat-navy-700">
                    การกระจายกลุ่มเปราะบางตามพื้นที่
                  </h2>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    แผนที่แสดงความเข้มข้นและความต้องการดูแลกลุ่มเปราะบางในแต่ละอำเภอ
                  </p>
                </div>

                <VulnerableMapPolygon
                  year={selectedYear}
                  selectedDistrictId={selectedDistrict}
                  selectedDistrictName={selectedDistrictName}
                />
              </div>
            </div>

          </div>
          {/* End of two-column layout */}

          {/* Trends Chart - Full width below */}
          <section className="mt-6">
              {trendsData?.trends && trendsData.trends.length > 0 && (
                <>
                  <AreaChartCard
                    title="แนวโน้มกลุ่มเปราะบาง 5 ปี"
                    description="จำนวนประชากรกลุ่มเปราะบาง การได้รับการดูแล และความต้องการเร่งด่วน"
                    data={trendsData.trends.map((item: any) => ({
                      year: `พ.ศ. ${item.year}`,
                      total: parseFloat(item.total_population),
                      immediate_care: parseFloat(item.total_immediate_care),
                      receiving_support: parseFloat(item.total_receiving_support),
                    }))}
                    areas={[
                      { dataKey: 'total', name: 'จำนวนทั้งหมด', color: 'blue' },
                      { dataKey: 'immediate_care', name: 'ต้องดูแลเร่งด่วน', color: 'orange' },
                      { dataKey: 'receiving_support', name: 'ได้รับการสนับสนุน', color: 'green' },
                    ]}
                    xAxisKey="year"
                    height={400}
                    isLoading={trendsQuery.isLoading}
                  />

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-korat-navy-50 border border-korat-navy-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">กลุ่มเปราะบางทั้งหมด</div>
                      <div className="text-2xl font-bold text-korat-navy-700 tabular-nums">
                        {formatNumber(parseFloat(data?.totals?.grand_total || '0'))} คน
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        ปี {selectedYear}
                      </div>
                    </div>

                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">ต้องดูแลเร่งด่วน</div>
                      <div className="text-2xl font-bold text-korat-orange-600 tabular-nums">
                        {formatNumber(parseFloat(data?.totals?.total_immediate_care || '0'))} คน
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {data?.totals?.total_immediate_care && data?.totals?.grand_total ? ((parseFloat(data.totals.total_immediate_care) / parseFloat(data.totals.grand_total)) * 100).toFixed(1) : '0'}% ของกลุ่มเปราะบาง
                      </div>
                    </div>

                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">ได้รับการสนับสนุน</div>
                      <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                        {formatNumber(parseFloat(data?.totals?.total_receiving_support || '0'))} คน
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {data?.totals?.total_receiving_support && data?.totals?.grand_total ? ((parseFloat(data.totals.total_receiving_support) / parseFloat(data.totals.grand_total)) * 100).toFixed(1) : '0'}% ของกลุ่มเปราะบาง
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>การวิเคราะห์:</strong> กราฟแสดงแนวโน้มจำนวนกลุ่มเปราะบาง การได้รับการดูแล และความต้องการเร่งด่วน
                      — ใช้วางแผนการจัดสรรทรัพยากรและบริการสาธารณสุข
                    </p>
                  </div>
                </>
              )}
          </section>

          {/* Groups Table - Full width below */}
          <section className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                จำแนกตามกลุ่มเปราะบาง
              </h2>
              <DataTable
                columns={columns}
                data={data?.by_group || []}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลกลุ่มเปราะบาง"
              />
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
